import { AppShell, type AppShellProps } from "@wayflyer/flyui";
import { Outlet, useLocation, useRoutes, type RouteObject } from "react-router";
import {
  UnknownRouteError,
  useSpaNavigate,
  OnboardingWorkflowTracker,
  ONBOARDING_STEP_IDS,
  ONBOARDING_SUBSTEP_IDS,
} from "shared-ui";
import { useCobrandingConfig, useCustomerAppConfig } from "undercarriage";

import { ResetScrollPosition } from "./components";
import { CONNECT_APP_ROUTE_PATHS } from "./components/constants";
import { LinearNavigationContextProvider } from "./hooks/navigation-next";
import {
  AboutYourBusinessPage,
  AddAnotherPage,
  AdditionalFundingPage,
  AdditionalInformationRequestPage,
  AirsReviewPage,
  ApplicationOverviewPage,
  ApplicationReviewPage,
  ApplicationSummaryPage,
  BankingPage,
  ConfigureNewFunderCustomerPage,
  ConnectionManagerNext,
  ContactDetailsPage,
  CreditCheckConsentPage,
  DocumentUploadManager,
  FundingLandingPage,
  IneligiblePage,
  RevenueEntryPage,
  SelectFundingPage,
  SelectPlatformsPage,
  UploadBankStatementsPage,
  VerifyDocumentUploadsManager,
} from "./pages";

const FundingApplicationLayout = ({
  countryOfIncorporation,
  customerDomain,
  secondaryLogoConfig,
  children,
}: {
  countryOfIncorporation?: string;
  customerDomain: string;
  secondaryLogoConfig?: AppShellProps["secondaryLogo"];
  children: React.ReactNode;
}) => {
  const location = useLocation();

  // Determine current step/substep based on page
  let currentStepId: string;
  let currentSubstepId: string | undefined;

  const isBusinessModelPage = location.pathname.includes(`/${CONNECT_APP_ROUTE_PATHS.BUSINESS_MODEL}`);
  const isSummaryPage = location.pathname.includes(`/${CONNECT_APP_ROUTE_PATHS.SUMMARY}`);

  if (isBusinessModelPage) {
    currentStepId = ONBOARDING_STEP_IDS.BUSINESS_DETAILS;
    currentSubstepId = ONBOARDING_SUBSTEP_IDS.BUSINESS_MODEL;
  } else if (isSummaryPage) {
    currentStepId = ONBOARDING_STEP_IDS.APPLICATION_REVIEW;
    currentSubstepId = undefined;
  } else {
    const searchParams = new URLSearchParams(location.search);
    const documentParam = searchParams.get("document");
    const isBankingPage = location.pathname.includes(`/${CONNECT_APP_ROUTE_PATHS.BANKING}`);
    const isCreditCheckPage = location.pathname.includes(
      `/${CONNECT_APP_ROUTE_PATHS.CONSENT}/${CONNECT_APP_ROUTE_PATHS.CREDIT_CHECK}`
    );
    const isUploadsPage = location.pathname.includes(`/${CONNECT_APP_ROUTE_PATHS.UPLOADS}`);
    const isSelectPlatformsPage = location.pathname.includes(`/${CONNECT_APP_ROUTE_PATHS.CONNECTIONS}`);

    currentStepId = ONBOARDING_STEP_IDS.CONNECTIONS_AND_DATA;

    if (isBankingPage) {
      currentSubstepId = ONBOARDING_SUBSTEP_IDS.BANK_ACCOUNT;
    } else if (isSelectPlatformsPage) {
      currentSubstepId = ONBOARDING_SUBSTEP_IDS.PLATFORMS;
    } else if (isCreditCheckPage) {
      currentSubstepId = ONBOARDING_SUBSTEP_IDS.SOFT_CREDIT_CHECK;
    } else if (isUploadsPage && documentParam) {
      currentSubstepId = documentParam;
    }
  }

  return (
    <OnboardingWorkflowTracker
      customerDomain={customerDomain}
      countryOfIncorporation={countryOfIncorporation}
      currentStepId={currentStepId}
      currentSubstepId={currentSubstepId}
      hasBusinessCountryData={true}
      hasRevenueData={true}
      hasBusinessLocationData={true}
      hasBusinessDetailsData={true}
      secondaryLogo={secondaryLogoConfig}
    >
      {children}
    </OnboardingWorkflowTracker>
  );
};

const Routes = () => {
  const {
    data: { customerDomain, countryOfIncorporation },
  } = useCustomerAppConfig();
  const { data: cobrandingLogos } = useCobrandingConfig();

  const { navigate } = useSpaNavigate("FundingApp");
  const { navigate: navigateCompanyCreation } = useSpaNavigate("CompanyCreation");

  const secondaryLogoConfig = {
    alt: cobrandingLogos?.partnerName || "",
    lightIconLogoUrl: cobrandingLogos?.lightIconLogoUrl,
    darkIconLogoUrl: cobrandingLogos?.darkIconLogoUrl,
    lightLogoUrl: cobrandingLogos?.lightLogoUrl,
    darkLogoUrl: cobrandingLogos?.darkLogoUrl,
  };

  const applicationJourneyRoutes: RouteObject = {
    children: [
      {
        path: "/",
        element: (
          <LinearNavigationContextProvider
            countryOfIncorporation={countryOfIncorporation ?? undefined}
            customerDomain={customerDomain}
          >
            <FundingApplicationLayout
              countryOfIncorporation={countryOfIncorporation ?? undefined}
              customerDomain={customerDomain}
              secondaryLogoConfig={secondaryLogoConfig}
            >
              <Outlet />
            </FundingApplicationLayout>
          </LinearNavigationContextProvider>
        ),
        children: [
          {
            path: CONNECT_APP_ROUTE_PATHS.CONNECTIONS,
            children: [
              {
                path: "",
                element: <SelectPlatformsPage />,
              },
              {
                path: CONNECT_APP_ROUTE_PATHS.APPLICATION_OVERVIEW,
                element: <ApplicationOverviewPage />,
              },
              {
                path: CONNECT_APP_ROUTE_PATHS.ADD_CONNECTION,
                element: <ConnectionManagerNext />,
              },
              {
                path: CONNECT_APP_ROUTE_PATHS.BANKING,
                children: [
                  {
                    path: "",
                    element: <BankingPage />,
                  },
                  {
                    path: CONNECT_APP_ROUTE_PATHS.BANK_STATEMENTS_UPLOAD,
                    element: <UploadBankStatementsPage />,
                  },
                ],
              },
              {
                path: CONNECT_APP_ROUTE_PATHS.ADD_ANOTHER,
                element: <AddAnotherPage />,
              },
            ],
          },
          {
            path: CONNECT_APP_ROUTE_PATHS.CONSENT,
            children: [
              {
                path: CONNECT_APP_ROUTE_PATHS.CREDIT_CHECK,
                element: <CreditCheckConsentPage />,
              },
            ],
          },
          {
            path: CONNECT_APP_ROUTE_PATHS.UPLOADS,
            element: <DocumentUploadManager />,
          },
          {
            path: CONNECT_APP_ROUTE_PATHS.VERIFY,
            element: <VerifyDocumentUploadsManager />,
          },
          {
            path: CONNECT_APP_ROUTE_PATHS.SUMMARY,
            element: <ApplicationSummaryPage />,
          },
        ],
      },
    ],
  };

  const routes: RouteObject[] = [
    // These routes should work outside a product request
    {
      path: "/",
      element: (
        <AppShell>
          <Outlet />
        </AppShell>
      ),
      children: [
        {
          path: "",
          element: <FundingLandingPage />,
        },
        {
          path: CONNECT_APP_ROUTE_PATHS.ADDITIONAL_FUNDING,
          // TODO fix this one - slipped
          element: <AdditionalFundingPage />,
        },
      ],
    },
    applicationJourneyRoutes,
    {
      path: "/",
      element: (
        <AppShell>
          <Outlet />
        </AppShell>
      ),
      children: [
        {
          path: CONNECT_APP_ROUTE_PATHS.IN_REVIEW,
          element: <ApplicationReviewPage />,
        },
        {
          path: `${CONNECT_APP_ROUTE_PATHS.IN_REVIEW}/${CONNECT_APP_ROUTE_PATHS.ADDITIONAL_INFORMATION_REQUEST}`,
          element: <AirsReviewPage />,
        },
        // Post application AIRs
        {
          path: `${CONNECT_APP_ROUTE_PATHS.IN_REVIEW}/${CONNECT_APP_ROUTE_PATHS.ADDITIONAL_INFORMATION_REQUEST}/:airId`,
          element: <AdditionalInformationRequestPage />,
        },
        // Tasks pre-additional funding application AIRs
        {
          path: `${CONNECT_APP_ROUTE_PATHS.ADDITIONAL_FUNDING}/${CONNECT_APP_ROUTE_PATHS.CUSTOMER_TASKS}/:airId`,
          element: <AdditionalInformationRequestPage />,
        },
      ],
    },
    {
      path: "/",
      element: (
        <FundingApplicationLayout
          countryOfIncorporation={countryOfIncorporation ?? undefined}
          customerDomain={customerDomain}
          secondaryLogoConfig={secondaryLogoConfig}
        >
          <Outlet />
        </FundingApplicationLayout>
      ),
      children: [
        {
          path: CONNECT_APP_ROUTE_PATHS.CONTACT_DETAILS,
          element: <ContactDetailsPage onContinue={() => navigate(CONNECT_APP_ROUTE_PATHS.REVENUE_ENTRY)} />,
        },
        {
          path: CONNECT_APP_ROUTE_PATHS.REVENUE_ENTRY,
          element: (
            <RevenueEntryPage
              onContinue={(applicationState) => {
                if (applicationState === "ineligible") {
                  navigate(CONNECT_APP_ROUTE_PATHS.INELIGIBLE);
                } else {
                  navigate(CONNECT_APP_ROUTE_PATHS.SELECT_FUNDING);
                }
              }}
              onBack={() => navigate(CONNECT_APP_ROUTE_PATHS.CONTACT_DETAILS)}
            />
          ),
        },
        {
          path: CONNECT_APP_ROUTE_PATHS.BUSINESS_MODEL,
          element: <AboutYourBusinessPage onBack={() => navigateCompanyCreation("business-details")} />,
        },
        {
          path: CONNECT_APP_ROUTE_PATHS.SELECT_FUNDING,
          element: (
            <LinearNavigationContextProvider
              countryOfIncorporation={countryOfIncorporation ?? undefined}
              customerDomain={customerDomain}
            >
              <SelectFundingPage />
            </LinearNavigationContextProvider>
          ),
        },
        {
          path: CONNECT_APP_ROUTE_PATHS.INELIGIBLE,
          element: <IneligiblePage />,
        },
      ],
    },
    {
      path: CONNECT_APP_ROUTE_PATHS.CONFIGURE_NEW_FUNDER_CUSTOMER,
      element: <ConfigureNewFunderCustomerPage />,
    },
    {
      path: "*",
      element: <UnknownRouteError />,
    },
  ];
  return <>{useRoutes(routes)}</>;
};

export const FundingApplicationAppRouter = () => {
  return (
    <>
      <ResetScrollPosition />
      <Routes />
    </>
  );
};
